export const RECORD_DURATION_MS = 6000;
// MediaRecorder 每 250ms 交付一个分片；8 秒滚动窗口因此最多保留 32 个分片。
export const RECORDING_TIMESLICE_MS = 250;

export const RECORDING_MIME_TYPES = Object.freeze([
  "video/mp4;codecs=avc1.42E01E",
  "video/mp4",
  "video/webm;codecs=vp9",
  "video/webm;codecs=vp8",
  "video/webm",
]);

export const getSupportedRecordingMimeType = (MediaRecorderApi) => {
  if (!MediaRecorderApi) return "";
  if (typeof MediaRecorderApi.isTypeSupported !== "function") return "";

  return (
    RECORDING_MIME_TYPES.find((mimeType) =>
      MediaRecorderApi.isTypeSupported(mimeType),
    ) || ""
  );
};

export const getVideoFileExtension = (mimeType = "") =>
  mimeType.toLowerCase().includes("mp4") ? "mp4" : "webm";

export const createRecordedVideoFile = (blob, side = "front") => {
  const mimeType = blob?.type || "video/webm";
  const extension = getVideoFileExtension(mimeType);

  return new File([blob], `identity-${side}-${Date.now()}.${extension}`, {
    type: mimeType,
  });
};

/**
 * MediaRecorder 的生命周期封装。
 *
 * autoStop=true：录满 durationMs 后自动停止；
 * autoStop=false + bufferWindowMs：持续录制但滚动淘汰旧分片，直到用户或自动流程 stop()。
 */
export class RecordingController {
  constructor({
    stream,
    durationMs = RECORD_DURATION_MS,
    autoStop = true,
    bufferWindowMs = 0,
    MediaRecorderApi = globalThis.MediaRecorder,
    onProgress,
    onStopping,
    onComplete,
    onError,
  }) {
    this.stream = stream;
    this.durationMs = durationMs;
    this.autoStop = autoStop;
    this.bufferWindowMs = bufferWindowMs;
    this.maxBufferedChunks = bufferWindowMs
      ? Math.max(1, Math.floor(bufferWindowMs / RECORDING_TIMESLICE_MS))
      : 0;
    this.MediaRecorderApi = MediaRecorderApi;
    this.onProgress = onProgress;
    this.onStopping = onStopping;
    this.onComplete = onComplete;
    this.onError = onError;
    this.recorder = null;
    this.chunks = [];
    this.startedAt = 0;
    this.progressTimer = null;
    this.stopTimer = null;
    this.cancelled = false;
  }

  start() {
    if (this.isRecording()) return false;
    if (!this.stream || !this.MediaRecorderApi) {
      this.handleError(new Error("MediaRecorder is not available."));
      return false;
    }

    this.cancelled = false;
    this.chunks = [];

    try {
      const mimeType = getSupportedRecordingMimeType(this.MediaRecorderApi);
      this.recorder = mimeType
        ? new this.MediaRecorderApi(this.stream, { mimeType })
        : new this.MediaRecorderApi(this.stream);
    } catch (error) {
      this.handleError(error);
      return false;
    }

    this.recorder.ondataavailable = (event) => {
      if (event.data?.size > 0) {
        this.chunks.push(event.data);
        // 超出窗口时从最旧分片开始删除，用户操作不被打断，最终文件仍受时长上限约束。
        if (
          this.maxBufferedChunks &&
          this.chunks.length > this.maxBufferedChunks
        ) {
          this.chunks.splice(0, this.chunks.length - this.maxBufferedChunks);
        }
      }
    };

    this.recorder.onerror = (event) => {
      this.handleError(event.error || new Error("Video recording failed."));
    };

    this.recorder.onstop = () => {
      this.clearTimers();
      if (this.cancelled) return;

      if (this.chunks.length === 0) {
        this.handleError(new Error("The recorded video is empty."));
        return;
      }

      // 只有 stop 事件到达后分片才完整，此时再一次性组装最终 Blob。
      const mimeType = this.recorder.mimeType || this.chunks[0].type;
      const blob = new Blob(this.chunks, {
        type: mimeType || "video/webm",
      });
      this.chunks = [];
      this.onProgress?.({
        elapsedMs: this.durationMs,
        remainingMs: 0,
        progress: 1,
      });
      this.onComplete?.(blob);
    };

    this.startedAt = Date.now();
    this.recorder.start(RECORDING_TIMESLICE_MS);
    if (this.onProgress) {
      this.emitProgress();
      this.progressTimer = globalThis.setInterval(() => this.emitProgress(), 100);
    }
    if (this.autoStop) {
      this.stopTimer = globalThis.setTimeout(() => this.stop(), this.durationMs);
    }

    return true;
  }

  emitProgress() {
    const elapsedMs = Math.min(Date.now() - this.startedAt, this.durationMs);
    this.onProgress?.({
      elapsedMs,
      remainingMs: Math.max(this.durationMs - elapsedMs, 0),
      progress: Math.min(elapsedMs / this.durationMs, 1),
    });
  }

  isRecording() {
    return this.recorder?.state === "recording";
  }

  stop() {
    if (!this.isRecording()) return false;
    this.clearTimers();
    this.onStopping?.();
    // 先请求当前尚未形成 timeslice 的尾部数据，再停止录制。
    this.recorder.requestData?.();
    this.recorder.stop();
    return true;
  }

  cancel() {
    this.cancelled = true;
    this.clearTimers();

    if (this.isRecording()) {
      this.recorder.stop();
    }

    this.chunks = [];
  }

  clearTimers() {
    globalThis.clearInterval(this.progressTimer);
    globalThis.clearTimeout(this.stopTimer);
    this.progressTimer = null;
    this.stopTimer = null;
  }

  handleError(error) {
    this.clearTimers();
    this.onError?.(error);
  }
}
